import { AfterViewInit, Component, effect, ElementRef, input, signal, ViewChild } from "@angular/core";
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from "@angular/forms";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { TranslatePipe } from "@ngx-translate/core";
import { BrowseBtnPosition } from "./models/browse-btn-position";

@Component({
  selector: "portal-upload-control",
  standalone: true,
  imports: [NgClass, TranslatePipe, NgTemplateOutlet],
  templateUrl: "./upload-control.component.html",
  styleUrl: "./upload-control.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: UploadControlComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: UploadControlComponent,
    },
  ],
})
export class UploadControlComponent implements ControlValueAccessor, Validator, AfterViewInit {
  @ViewChild("label") readonly fileLabel!: ElementRef<HTMLLabelElement>;
  @ViewChild("fileInput") readonly fileInput!: ElementRef<HTMLInputElement>;
  readonly acceptedFileTypes = input<string>();
  readonly isInvalid = input<boolean>(false);
  readonly isValid = input<boolean>(false);
  readonly isDisabled = input<boolean>(false);

  readonly browseBtnLabel = input<string>("GAST-PORTAL-NG.UPLOAD_CONTROL.BROWSE_BTN");
  readonly placeholderLabel = input<string>("GAST-PORTAL-NG.UPLOAD_CONTROL.PLACEHOLDER");
  readonly btnPosition = input<BrowseBtnPosition>("right");

  file = signal<File | null>(null);
  disabled = signal<boolean>(false);
  #touched = signal<boolean>(false);

  constructor() {
    effect(
      () => {
        this.disabled.set(this.isDisabled());
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewInit(): void {
    // Workaround to be able to focus a hidden input.
    this.fileLabel.nativeElement.addEventListener("focus", () => {
      this.fileInput.nativeElement.focus();
    });
    this.fileInput.nativeElement.addEventListener("blur", () => {
      this.fileInput.nativeElement.blur();
    });
  }

  onCanceled(): void {
    this.#markAsTouched();
  }

  onFileChange(fileInput: Event): void {
    this.#markAsTouched();
    const target = fileInput.target as HTMLInputElement;
    this.file.set((!!target && !!target.files && target.files[0]) || null);
    this.#onStateChanged(this.file());
  }

  registerOnChange(onChange: never): void {
    this.#onStateChanged = onChange;
  }

  registerOnTouched(onTouched: never): void {
    this.#onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  writeValue(file: File | null): void {
    this.file.set(file);
  }

  validate(): ValidationErrors | null {
    return null;
  }

  #markAsTouched() {
    if (!this.#touched()) {
      this.#onTouched();
      this.#touched.set(true);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  #onStateChanged = (file: File | null): void => {
    return;
  };
  #onTouched = (): void => {
    return;
  };
}
